import { IRosterShift } from '../types';
import moment from 'moment-timezone';
import { FieldLabel, Text } from 'common-components/typography';
import { Avatar } from 'antd';
import { HyperlinkButton } from 'common-components/buttons';
import React, { useState } from 'react';
import { BookingTypeIcon } from 'common-components/tags';
import { BookingStatus, BookingType, ServiceType, ShiftSlotStatus } from 'utilities/enum-utils';
import { IRootDispatch } from 'stores/rematch/root-store';
import { useDispatch } from 'react-redux';
import CommonUtils from 'utilities/common-utils';
import PermissionUtils from 'utilities/permission-utils';
import SpinningLoader from 'common-components/loading/SpinningLoader';
import { RecommendedPanel } from '../components/recommended-panel';
import { useFetchSessionCapacity } from 'stores/hooks/query-hooks/use-query-fetch-session-capacity';
import _ from 'lodash';
import { Link } from 'react-router-dom';
import { Box, Button, Group, Paper, ScrollArea, ThemeIcon, UnstyledButton, Collapse, Title } from '@good/ui/core';
import { usePortalUser } from 'utilities/react-hooks/use-portal-user';
import { WorkerShiftStatusBadge } from '../cards/worker-shift-status-badge';
import { getShiftSlotTheme } from '../components/roster-utilities';
import { ChevronDown, ChevronUp } from '@good/icons';
import RemoveWorkerBookingInline from 'views/bookings-v2/bookings-roster-view/drawers/remove-worker-booking-inline';
import RemoveWorkerGroupInline from './remove-worker-group-inline';

interface WorkerShiftDrawerProps {
  shift: IRosterShift;
  refreshAndCloseData: () => void;
  setCanClosePopover: (value: boolean) => void;
}

export const WorkerShiftDrawer = ({ shift, refreshAndCloseData, setCanClosePopover }: WorkerShiftDrawerProps) => {
  const {
    shiftStartDateTime,
    shiftEndDateTime,
    serviceName,
    workerLastName,
    workerFirstName,
    attachmentUrl,
    workerAttachmentUrl,
    shiftSlotStatus,
    customerFirstName,
    customerLastName,
    serviceId,
    serviceDateTimeId,
  } = shift;
  const { data: sessionData, isLoading: sessionIsLoading } = useFetchSessionCapacity(
    {
      serviceId,
      serviceDateTimeId,
    },
    shift.serviceType === ServiceType.GROUP,
  );
  const portalUser = usePortalUser();
  const [filtersCollapsed, setFiltersCollapsed] = useState(true);
  const { navigationStore: navigationDispatch, bookingsStore: bookingDispatch } = useDispatch<IRootDispatch>();
  const { setSelectedSideNavMenuKeys } = navigationDispatch;
  const { setSelectedBookingItem } = bookingDispatch;
  const mStartDate = moment(shiftStartDateTime);
  const mEndDate = moment(shiftEndDateTime);
  const dateDisplay = mStartDate.format('dddd, D MMMM YYYY');
  const timeDisplay = (
    <>
      <b>{mStartDate.format('h:mm a')}</b> - {mEndDate.format('h:mm a')}
    </>
  );
  const workerFullName = `${workerFirstName} ${workerLastName}`;
  const customerFullName = `${customerFirstName} ${customerLastName}`;
  const { bgColor, borderColor } = getShiftSlotTheme(shiftSlotStatus);

  const goToShift = () => {
    if (shift.serviceType === ServiceType.GROUP) {
      setSelectedSideNavMenuKeys(['/services']);
    } else {
      setSelectedSideNavMenuKeys(['/services']);
    }
  };

  const goToService = () => {
    setSelectedSideNavMenuKeys(['/services']);
  };

  const goToSession = () => {
    setSelectedSideNavMenuKeys(['/services']);
  };

  const doRefresh = async (doRefreshData = false) => {
    setCanClosePopover(true);
    setSelectedBookingItem(null);
    if (doRefreshData) {
      await refreshAndCloseData();
    }
  };

  const hasEditWorkerPermissions = PermissionUtils.validatePermission(
    'EditWorkerInBooking',
    portalUser.permissions.permissionRoles,
    shift.serviceDepartmentId,
    shift.serviceId,
  );

  const canRemove =
    hasEditWorkerPermissions &&
    ((shift.bookingStatus !== BookingStatus.ARCHIVED &&
      shift.serviceType === ServiceType.INDIVIDUAL &&
      (shift.bookingStatus === BookingStatus.PENDING ||
        shift.bookingStatus === BookingStatus.ACCEPTED ||
        shift.bookingStatus === BookingStatus.CONFIRMED)) ||
      (shift.serviceType === ServiceType.GROUP &&
        shift.shiftSlotStatus !== ShiftSlotStatus.APPROVED &&
        shift.shiftSlotStatus !== ShiftSlotStatus.SENT_TO_FINANCE));

  const showActions =
    canRemove && (shift.serviceType === ServiceType.INDIVIDUAL || shift.serviceType === ServiceType.GROUP);

  return (
    <ScrollArea h='100%'>
      <div className='flex-column line-height-100 select-none'>
        <Box pr='md'>
          <Paper
            withBorder
            p='md'
            shadow='lg'
            bg={bgColor}
            style={{
              borderColor: borderColor,
              borderLeft: 'none',
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
            }}
          >
            <Group gap='xs' justify='space-between' wrap='nowrap'>
              <div className='mb-x-small line-height-100 inline-block'>
                <WorkerShiftStatusBadge status={shiftSlotStatus} />
              </div>
              <Link
                to={{
                  pathname:
                    shift.serviceType === ServiceType.GROUP
                      ? `/group-service/${shift.serviceId}/session/details/${shift.serviceDateTimeId}`
                      : `/bookings/details/${shift.bookingId}`,
                  state: {
                    selectedTab: 'TEAM-MEMBERS',
                    from: { url: '/bookings?view=calendar', linkLabel: 'Back to Calendar' },
                  },
                }}
              >
                <Button onClick={goToShift}>View</Button>
              </Link>
            </Group>
            <div className='line-height-120 mb-medium'>
              <Text size='x-large' lineHeight={135} weight='bold'>
                {dateDisplay}
              </Text>
              <br />
              <Text size='x-large' lineHeight={135}>
                {timeDisplay}
              </Text>
              <br />
              <Text size='x-large' lineHeight={135}>
                <Text size='x-large' color={'secondary'}>
                  {CommonUtils.formatDurationString(mStartDate, mEndDate)}
                </Text>
              </Text>
              <br />
              <div className={'flex-row align-center mt-small'}>
                <BookingTypeIcon
                  type={
                    shift.bookingType === BookingType.ACTIVITY_RECORD ? BookingType.ACTIVITY_RECORD : shift.serviceType
                  }
                  className={'mr-small'}
                />
                <Text color={'tertiary'}>{CommonUtils.formatServiceTypeStatus(shift)}</Text>
              </div>
              {shift.serviceType === ServiceType.INDIVIDUAL || !shift.scheduleName ? (
                <Link
                  to={
                    shift.serviceType === ServiceType.GROUP
                      ? `/group-service/details/${serviceId}`
                      : `/service/details/${serviceId}`
                  }
                >
                  <HyperlinkButton color={'black'} onClick={goToService}>
                    {serviceName}
                  </HyperlinkButton>
                </Link>
              ) : (
                <Text>
                  {shift.scheduleName}{' '}
                  <Link
                    to={
                      shift.serviceType === ServiceType.GROUP
                        ? `/group-service/details/${serviceId}`
                        : `/service/details/${serviceId}`
                    }
                  >
                    <HyperlinkButton color={'secondary'} onClick={goToService}>
                      ({shift.serviceName})
                    </HyperlinkButton>
                  </Link>
                </Text>
              )}
              {shift.serviceType === ServiceType.GROUP && (
                <div>
                  <Link to={`/group-service/${serviceId}/session/details/${serviceDateTimeId}`}>
                    <HyperlinkButton onClick={goToSession}>Go to session</HyperlinkButton>
                  </Link>
                </div>
              )}
            </div>

            {shift.serviceType === ServiceType.GROUP && (
              <div className={'mb-large'}>
                <div className='mt-x-small'>
                  {sessionIsLoading ? (
                    <SpinningLoader size={20} message={'Fetching recommended capacity...'} />
                  ) : (
                    sessionData?.data && (
                      <RecommendedPanel
                        recommendedTotalShiftSlot={sessionData.data.recommendedTotalShiftSlot}
                        totalAssignedShiftSlot={sessionData.data.totalAssignedShiftSlot}
                      />
                    )
                  )}
                </div>
              </div>
            )}

            {shift.bookingId && (
              <div className='mb-large'>
                <FieldLabel text={'CUSTOMER'} />
                <div className='flex-row align-center mt-x-small'>
                  <Avatar icon='user' className='mr-small' shape='circle' src={attachmentUrl} />
                  <Text color='secondary'>{customerFullName}</Text>
                </div>
              </div>
            )}

            {shift.serviceType === ServiceType.GROUP && shift.shiftCustomerAssignments && (
              <div className='mb-large'>
                <FieldLabel text={'CUSTOMERS'} />
                <div className='mt-x-small' style={{ overflow: 'auto', maxHeight: '120px' }}>
                  {_.map(shift.shiftCustomerAssignments, (assignment) => {
                    return (
                      <div className='flex-row align-center mt-x-small'>
                        <div className='flex-row line-height-120 mb-small'>
                          <Avatar
                            icon='user'
                            size='small'
                            className='mr-small'
                            src={assignment.customerAttachmentUrl}
                          />
                          <Text size='regular'>
                            <span className={'text-weight-bold'}>
                              {assignment.customerFirstName} {assignment.customerLastName}
                            </span>
                            <br />
                            <span>
                              {moment.tz(assignment.startDateTime, shift.timezone).format('h:mm a')}{' '}
                              {moment.tz(assignment.endDateTime, shift.timezone).format('h:mm a')}
                            </span>
                          </Text>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className='mb-large'>
              <FieldLabel text={'TEAM MEMBER'} />
              <div className='flex-row align-center mt-x-small'>
                <Avatar icon='user' className='mr-small' shape='square' src={workerAttachmentUrl} />
                <Text color='secondary'>{workerFullName}</Text>
              </div>
            </div>
          </Paper>
        </Box>
      </div>
      {showActions && (
        <Box p='md'>
          <div>
            <UnstyledButton w='100%' onClick={() => setFiltersCollapsed(!filtersCollapsed)}>
              <Group justify='space-between'>
                <Group gap={6}>
                  <Title order={4}>Remove team member from booking</Title>
                  <ThemeIcon variant='transparent' size='sm'>
                    {filtersCollapsed ? <ChevronDown /> : <ChevronUp />}
                  </ThemeIcon>
                </Group>
              </Group>
            </UnstyledButton>
            <Collapse in={!filtersCollapsed}>
              {shift.serviceType === ServiceType.INDIVIDUAL && (
                <RemoveWorkerBookingInline
                  onRemoved={doRefresh}
                  bookingType={shift.bookingType}
                  selectedBooking={shift}
                />
              )}
              {shift.serviceType === ServiceType.GROUP && (
                <RemoveWorkerGroupInline
                  onRemoved={doRefresh}
                  shiftSlot={{
                    ...shift,
                    firstName: shift.workerFirstName,
                    lastName: shift.workerLastName,
                    attachmentUrl: shift.workerAttachmentUrl,
                  }}
                  session={shift}
                />
              )}
            </Collapse>
          </div>
        </Box>
      )}
    </ScrollArea>
  );
};
